import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { HC } from "../hr/theme";
import { Btn, Pill } from "../hr/ui";
import { DataTable, num, dt } from "./crmui";
import { toast } from "sonner";
import { Plus, Play, Pause, Users, Zap } from "lucide-react";

const TRIGGERS = [
  ["customer_registered", "Customer registered"],
  ["purchase_completed", "Purchase completed"],
  ["inactivity", "Inactivity threshold"],
  ["segment_entry", "Segment entry"],
  ["loyalty_tier", "Loyalty tier reached"],
];
const STEP_KINDS = [["wait", "Wait"], ["condition", "Condition"], ["action", "Send communication"], ["exit", "Exit"]];
const CONDITIONS = [["has_purchased", "Has purchased"], ["has_not_purchased", "Has not purchased"],
                     ["loyalty_tier", "Loyalty tier is"], ["location", "Location is"], ["favourite_category", "Favourite category is"]];

export default function Journeys() {
  const [rows, setRows] = useState([]);
  const [segments, setSegments] = useState([]);
  const [edit, setEdit] = useState(null);
  const [members, setMembers] = useState(null);

  const load = useCallback(() => {
    api.get("/crm/journeys").then(setRows).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); api.get("/crm/segments").then(setSegments).catch(() => {}); }, [load]);

  const save = async () => {
    if (!edit.name) { toast.error("Journey name is required"); return; }
    try {
      if (edit.id) await api.put(`/crm/journeys/${edit.id}`, edit);
      else await api.post("/crm/journeys", edit);
      toast.success("Journey saved"); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStep = (i, patch) => {
    const steps = [...(edit.steps || [])];
    steps[i] = { ...steps[i], ...patch };
    setEdit({ ...edit, steps });
  };

  return (
    <div className="space-y-4" data-testid="crm-journeys-page">
      <div className="flex items-center gap-2">
        <div className="text-[12px]" style={{ color: HC.soft }}>
          Journeys advance every 15 minutes via the platform scheduler — or run them now.
        </div>
        <div className="flex-1" />
        <Btn variant="outline" data-testid="journey-run-now"
          onClick={async () => {
            try { const r = await api.post("/crm/journeys/run-now", {}); toast.success(`Enrolled ${r.enrolled}, advanced ${r.advanced}, completed ${r.completed}`); load(); }
            catch (e) { toast.error(formatApiError(e)); }
          }}><Zap size={14} /> Run journeys now</Btn>
        <Btn onClick={() => setEdit({ trigger: { type: "customer_registered", within_days: 7 }, steps: [], exit_conditions: [], status: "draft" })}
          data-testid="journey-new"><Plus size={14} /> New Journey</Btn>
      </div>

      <DataTable testid="crm-journeys-table" rows={rows}
        columns={[["name", "Journey"], ["trigger_label", "Trigger"], ["steps_count", "Steps"], ["status", "Status"],
                  ["enrolled", "Enrolled"], ["completed", "Completed"], ["exited", "Exited"], ["actions", "Actions"]]}
        render={{
          trigger_label: (r) => (TRIGGERS.find(([t]) => t === r.trigger?.type) || ["", "—"])[1],
          steps_count: (r) => (r.steps || []).length,
          status: (r) => <Pill status={r.status === "active" ? "active" : "draft"} text={r.status} />,
          enrolled: (r) => num(r.stats?.enrolled), completed: (r) => num(r.stats?.completed), exited: (r) => num(r.stats?.exited),
          actions: (r) => (
            <span className="flex gap-1.5">
              <button data-testid={`journey-toggle-${r.id.slice(0, 4)}`} className="h-7 px-2 rounded-md border text-[11.5px]" style={{ borderColor: HC.border }}
                onClick={async (e) => {
                  e.stopPropagation();
                  try { const res = await api.post(`/crm/journeys/${r.id}/activate`, { active: r.status !== "active" });
                    toast.success(`Journey ${res.status}${res.enrolled ? ` — ${res.enrolled} enrolled` : ""}`); load(); }
                  catch (err) { toast.error(formatApiError(err)); }
                }}>{r.status === "active" ? <Pause size={12} /> : <Play size={12} />}</button>
              <button data-testid={`journey-members-${r.id.slice(0, 4)}`} className="h-7 px-2 rounded-md border text-[11.5px]" style={{ borderColor: HC.border }}
                onClick={async (e) => { e.stopPropagation(); setMembers(await api.get(`/crm/journeys/${r.id}/members`)); }}><Users size={12} /></button>
            </span>
          ),
        }}
        onRowClick={(r) => setEdit(JSON.parse(JSON.stringify(r)))} empty="No journeys yet" />

      {edit && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: "rgba(51,34,30,0.45)" }}>
          <div className="bg-white rounded-2xl border w-full max-w-2xl p-5 space-y-3 my-8" style={{ borderColor: HC.border }} data-testid="journey-builder">
            <h3 className="font-brand font-bold text-lg" style={{ color: HC.text }}>{edit.id ? "Edit journey" : "New journey"}</h3>
            <input placeholder="Journey name *" value={edit.name || ""} data-testid="journey-name"
              onChange={(e) => setEdit({ ...edit, name: e.target.value })}
              className="h-10 px-3 w-full rounded-md border text-[13px]" style={{ borderColor: HC.border }} />

            <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: HC.border, background: HC.panelSoft }}>
              <div className="text-[10px] font-bold tracking-[0.14em]" style={{ color: HC.soft }}>TRIGGER</div>
              <div className="flex flex-wrap gap-2">
                <select value={edit.trigger?.type} data-testid="journey-trigger"
                  onChange={(e) => setEdit({ ...edit, trigger: { type: e.target.value, within_days: 7, days: 30 } })}
                  className="h-9 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }}>
                  {TRIGGERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                {edit.trigger?.type === "inactivity" && (
                  <input type="number" value={edit.trigger.days || 30} data-testid="journey-trigger-days"
                    onChange={(e) => setEdit({ ...edit, trigger: { ...edit.trigger, days: Number(e.target.value) } })}
                    className="h-9 px-2 w-24 rounded-md border text-[12px]" style={{ borderColor: HC.border }} />
                )}
                {edit.trigger?.type === "segment_entry" && (
                  <select value={edit.trigger.segment_id || ""} data-testid="journey-trigger-segment"
                    onChange={(e) => setEdit({ ...edit, trigger: { ...edit.trigger, segment_id: e.target.value } })}
                    className="h-9 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }}>
                    <option value="">Select segment…</option>
                    {segments.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                )}
                {["customer_registered", "purchase_completed"].includes(edit.trigger?.type) && (
                  <span className="text-[12px] self-center" style={{ color: HC.soft }}>within
                    <input type="number" value={edit.trigger.within_days || 7}
                      onChange={(e) => setEdit({ ...edit, trigger: { ...edit.trigger, within_days: Number(e.target.value) } })}
                      className="h-8 px-2 w-16 mx-1 rounded-md border text-[12px]" style={{ borderColor: HC.border }} /> days
                  </span>
                )}
              </div>
            </div>

            {(edit.steps || []).map((s, i) => (
              <div key={i} className="rounded-xl border p-3 space-y-2" style={{ borderColor: HC.border }} data-testid={`journey-step-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold" style={{ color: HC.gold }}>STEP {i + 1}</span>
                  <select value={s.kind} onChange={(e) => setStep(i, { kind: e.target.value })} data-testid={`journey-step-kind-${i}`}
                    className="h-8 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }}>
                    {STEP_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <div className="flex-1" />
                  <button onClick={() => setEdit({ ...edit, steps: edit.steps.filter((_, x) => x !== i) })}
                    className="text-[12px]" style={{ color: HC.red }}>Remove</button>
                </div>
                {s.kind === "wait" && (
                  <input type="number" placeholder="Hours to wait" value={s.hours || ""} data-testid={`journey-step-hours-${i}`}
                    onChange={(e) => setStep(i, { hours: Number(e.target.value) })}
                    className="h-9 px-2 w-40 rounded-md border text-[12px]" style={{ borderColor: HC.border }} />
                )}
                {s.kind === "condition" && (
                  <div className="flex flex-wrap gap-2">
                    <select value={s.condition || ""}
                      onChange={(e) => setStep(i, { condition: e.target.value })}
                      data-testid={`journey-step-condition-${i}`}
                      className="h-9 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }}>
                      <option value="">Select condition…</option>
                      {CONDITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <input placeholder="Value (if needed)" value={s.value || ""} data-testid={`journey-step-value-${i}`}
                      onChange={(e) => setStep(i, { value: e.target.value })}
                      className="h-9 px-2 w-40 rounded-md border text-[12px]" style={{ borderColor: HC.border }} />
                    <label className="text-[12px] flex items-center gap-1.5" style={{ color: HC.muted }}>
                      <input type="checkbox" checked={!!s.else_exit} onChange={(e) => setStep(i, { else_exit: e.target.checked })} />
                      Exit journey if not met
                    </label>
                  </div>
                )}
                {s.kind === "action" && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <select value={s.channel || "email"} onChange={(e) => setStep(i, { channel: e.target.value })}
                        data-testid={`journey-step-channel-${i}`} className="h-9 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }}>
                        {["email", "sms", "push", "whatsapp"].map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input placeholder="Subject" value={s.subject || ""} data-testid={`journey-step-subject-${i}`}
                        onChange={(e) => setStep(i, { subject: e.target.value })}
                        className="h-9 px-2 flex-1 rounded-md border text-[12px]" style={{ borderColor: HC.border }} />
                    </div>
                    <textarea placeholder="Message body" rows={2} value={s.body || ""} data-testid={`journey-step-body-${i}`}
                      onChange={(e) => setStep(i, { body: e.target.value })}
                      className="px-2 py-1.5 w-full rounded-md border text-[12px]" style={{ borderColor: HC.border }} />
                  </div>
                )}
              </div>
            ))}
            <Btn variant="outline" data-testid="journey-add-step"
              onClick={() => setEdit({ ...edit, steps: [...(edit.steps || []), { kind: "wait", hours: 24 }] })}>
              <Plus size={14} /> Add step
            </Btn>

            <div className="flex justify-end gap-2">
              <Btn variant="outline" onClick={() => setEdit(null)}>Cancel</Btn>
              <Btn onClick={save} data-testid="journey-save">Save journey</Btn>
            </div>
          </div>
        </div>
      )}

      {members && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: "rgba(51,34,30,0.45)" }}>
          <div className="bg-white rounded-2xl border w-full max-w-2xl p-5 space-y-3 my-8" style={{ borderColor: HC.border }} data-testid="journey-members-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-brand font-bold text-lg" style={{ color: HC.text }}>Journey membership ({members.total})</h3>
              <Btn variant="outline" onClick={() => setMembers(null)}>Close</Btn>
            </div>
            <DataTable testid="journey-members-table" rows={members.rows}
              columns={[["customer_name", "Customer"], ["status", "Status"], ["step_index", "Step"],
                        ["next_run_at", "Next action"], ["enrolled_at", "Enrolled"]]}
              render={{ next_run_at: (r) => dt(r.next_run_at), enrolled_at: (r) => dt(r.enrolled_at) }}
              empty="Nobody enrolled yet" />
          </div>
        </div>
      )}
    </div>
  );
}
